/**
 * DepositStatusBadge — badge สถานะมัดจำของ rental_item + บรรทัดรองอธิบาย (Deposit v2 Phase 1)
 *
 * 6 กรณี (ตาม contract):
 *   PENDING                      รอชำระ        bg-warning-100 text-warning-700
 *   PAID                         ชำระแล้ว      bg-success-100 text-success-700   · "ชำระ dd/mm/yyyy"
 *   PENDING_REFUND (hold=false)  รอคืนมัดจำ    bg-orange-100  text-orange-700    · "รอคืนมาแล้ว N วันทำการ · กำหนด dd/mm/yyyy"
 *                                                                                  + "เกินกำหนด 15 วันทำการ" สีแดงเมื่อ overdue
 *   PENDING_REFUND (hold=true)   คงมัดจำไว้    bg-purple-100  text-purple-700    · "คงไว้ตั้งแต่ dd/mm/yyyy · note"
 *   RETURNED                     คืนแล้ว       bg-info-100    text-info-700      · "คืน dd/mm/yyyy · วิธี อ้างอิง · โดย ชื่อ"
 *   WAIVED                       ยกเว้น        bg-secondary-100 text-secondary-700
 *
 * ค่าวันทำการ/กำหนด/overdue อ่านจาก computed field ของ backend เท่านั้น (ไม่คำนวณฝั่ง client)
 */
import React from 'react';
import { cn } from '../../utils/cn';
import {
  DEPOSIT_REFUND_SLA_BUSINESS_DAYS,
  RentalItemDepositFields,
  formatDateDMY,
  getDepositRefundMethodLabel,
} from '../../services/depositService';

export interface DepositStatusMeta {
  key: 'PENDING' | 'PAID' | 'PENDING_REFUND' | 'HOLD' | 'RETURNED' | 'WAIVED';
  label: string;
  className: string;
}

export const getDepositStatusMeta = (item: RentalItemDepositFields): DepositStatusMeta => {
  switch (item.deposit_status) {
    case 'PAID':
      return { key: 'PAID', label: 'ชำระแล้ว', className: 'bg-success-100 text-success-700' };
    case 'PENDING_REFUND':
      return item.deposit_hold
        ? { key: 'HOLD', label: 'คงมัดจำไว้', className: 'bg-purple-100 text-purple-700' }
        : { key: 'PENDING_REFUND', label: 'รอคืนมัดจำ', className: 'bg-orange-100 text-orange-700' };
    case 'RETURNED':
      return { key: 'RETURNED', label: 'คืนแล้ว', className: 'bg-info-100 text-info-700' };
    case 'WAIVED':
      return { key: 'WAIVED', label: 'ยกเว้น', className: 'bg-secondary-100 text-secondary-700' };
    case 'PENDING':
    default:
      return { key: 'PENDING', label: 'รอชำระ', className: 'bg-warning-100 text-warning-700' };
  }
};

interface DepositStatusBadgeProps {
  item: RentalItemDepositFields;
  /** แสดงบรรทัดรอง (วันที่/วันทำการ/ผู้บันทึก) — ปิดเมื่อพื้นที่จำกัด เช่น ตารางรายการ */
  showDetail?: boolean;
  /** SLA ที่ใช้ในข้อความ "เกินกำหนด N วันทำการ" (ค่าจริงอยู่ที่ backend; ส่งมาเมื่ออ่านจาก settings ได้) */
  slaBusinessDays?: number;
  align?: 'left' | 'center';
  className?: string;
}

const DepositStatusBadge: React.FC<DepositStatusBadgeProps> = ({
  item,
  showDetail = true,
  slaBusinessDays = DEPOSIT_REFUND_SLA_BUSINESS_DAYS,
  align = 'left',
  className,
}) => {
  const meta = getDepositStatusMeta(item);

  const detail: React.ReactNode[] = [];
  if (showDetail) {
    if (meta.key === 'PAID' && item.deposit_paid_date) {
      detail.push(
        <span key="paid" className="text-success-600">ชำระ {formatDateDMY(item.deposit_paid_date)}</span>
      );
    }
    if (meta.key === 'PENDING_REFUND') {
      const parts: string[] = [];
      if (item.deposit_refund_business_days_elapsed !== null && item.deposit_refund_business_days_elapsed !== undefined) {
        parts.push(`รอคืนมาแล้ว ${item.deposit_refund_business_days_elapsed} วันทำการ`);
      }
      if (item.deposit_refund_due_date) {
        parts.push(`กำหนด ${formatDateDMY(item.deposit_refund_due_date)}`);
      }
      if (parts.length > 0) {
        detail.push(<span key="pending" className="text-orange-600">{parts.join(' · ')}</span>);
      }
      if (item.deposit_refund_overdue) {
        detail.push(
          <span key="overdue" className="text-danger-600 font-medium" data-testid="deposit-overdue">
            เกินกำหนด {slaBusinessDays} วันทำการ
          </span>
        );
      }
    }
    if (meta.key === 'HOLD') {
      const parts: string[] = [];
      if (item.deposit_hold_at) parts.push(`คงไว้ตั้งแต่ ${formatDateDMY(item.deposit_hold_at)}`);
      if (item.deposit_hold_note) parts.push(item.deposit_hold_note);
      if (parts.length > 0) {
        detail.push(<span key="hold" className="text-purple-600">{parts.join(' · ')}</span>);
      }
    }
    if (meta.key === 'RETURNED') {
      const parts: string[] = [];
      if (item.deposit_returned_date) parts.push(`คืน ${formatDateDMY(item.deposit_returned_date)}`);
      const method = getDepositRefundMethodLabel(item.deposit_refund_method);
      const methodRef = [method, item.deposit_refund_reference].filter(Boolean).join(' ');
      if (methodRef) parts.push(methodRef);
      if (item.deposit_returned_by_name) parts.push(`โดย ${item.deposit_returned_by_name}`);
      if (parts.length > 0) {
        detail.push(<span key="returned" className="text-info-600">{parts.join(' · ')}</span>);
      }
    }
  }

  return (
    <div className={cn('inline-flex flex-col gap-0.5 max-w-full', align === 'center' ? 'items-center text-center' : 'items-start', className)}>
      <span
        className={cn('inline-block text-xs px-2 py-0.5 rounded-full whitespace-nowrap', meta.className)}
        data-testid="deposit-status-badge"
        data-status={meta.key}
      >
        {meta.label}
      </span>
      {detail.length > 0 && (
        <div className={cn('flex flex-col gap-0.5 text-xs leading-tight break-words', align === 'center' ? 'items-center' : 'items-start')}>
          {detail}
        </div>
      )}
    </div>
  );
};

export default DepositStatusBadge;
